import { FunctionalComponent, createRef, h } from "preact";
import VimeoPlayer, { Options as VimeoPlayerOption } from "@vimeo/player";
import { useEffect, useState } from "preact/hooks";

import style from "./style.scss";

interface Props {
  url: string;
  background?: boolean;
  className?: string;
  maxHeight?: string;
  playerOptions?: VimeoPlayerOption;
}

const DEFAULT_OPTIONS = {
  byline: false,
  loop: true,
  portrait: false,
  title: false,
  width: 640,
};

const Vimeo: FunctionalComponent<Props> = (props: Props) => {
  const addedClassName = props.className ?? "";
  const vimeoIframeRef = createRef();
  const player = createRef<VimeoPlayer>();
  const [videoWidth, setVideoWidth] = useState<number>(props.playerOptions?.width ?? 640);
  const [videoHeight, setVideoHeight] = useState<number>(props.playerOptions?.height ?? 360);

  useEffect(() => {
    if (player.current) return;

    player.current = new VimeoPlayer(vimeoIframeRef.current, {
      ...DEFAULT_OPTIONS,
      background: props.background ?? false,
      ...(props.playerOptions ?? {}),
      url: props.url,
    });

    if (props.playerOptions?.height && props.playerOptions?.width) return;

    void Promise.all([
      player.current.getVideoWidth(),
      player.current.getVideoHeight(),
    ]).then((dimensions) => {
      setVideoWidth(dimensions[0]);
      setVideoHeight(dimensions[1]);
    });
  }, [
    player,
    vimeoIframeRef,
    props.background,
    props.url,
    props.playerOptions,
  ]);

  const ratio = videoHeight / videoWidth;

  return (
    <div
      class={`${style.root} ${addedClassName}`}
      data-background={String(!!props.background)}
      style={{
        maxWidth: props.maxHeight
          ? `calc((${props.maxHeight}) / ${ratio})`
          : undefined,
      }}
    >
      <div class={style.positioningRoot}>
        <div class={style.positioningNode}>
          <div
            ref={vimeoIframeRef}
            class={style.player}
            style={{
              paddingBottom: `${ratio * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Vimeo;
